import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { findBlogBySlug, findActiveBlogs, findImage } from '../../api/apiService';
import dateIcon from '../../assets/date.svg';

export default function BlogDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blogPost, setBlogPost] = useState(null);
    const [allBlogs, setAllBlogs] = useState([]);
    const [imageUrl, setImageUrl] = useState(null);
    const [specificImageUrl, setSpecificImageUrl] = useState(null);
    const [prevPost, setPrevPost] = useState(null);
    const [nextPost, setNextPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');

    const renderImage = async (id) => {
        if (!id) return null;
        try {
            const res = await findImage(id);
            return res?.data?.url;
        } catch (err) {
            return null;
        }
    };

    const findData = async () => {
        setIsLoading(true);
        setStatusMessage('');

        if (!slug || slug === 'undefined') {
            setBlogPost(null);
            setAllBlogs([]);
            setPrevPost(null);
            setNextPost(null);
            setImageUrl(null);
            setSpecificImageUrl(null);
            setStatusMessage('The requested blog could not be found.');
            setIsLoading(false);
            return;
        }

        try {
            const res = await findBlogBySlug(slug);

            if (res?.data && res.data.length > 0) {
                const post = res.data[0];

                if (post?.isActive) {
                    setBlogPost(post);

                    const allRes = await findActiveBlogs();
                    const sortedBlogs = (allRes?.data || []).sort((a, b) => {
                        return new Date(b.date) - new Date(a.date);
                    });
                    setAllBlogs(sortedBlogs);

                    const currentIndex = sortedBlogs.findIndex(blog => blog.slug === slug);

                    // Reset prev/next before setting
                    setPrevPost(null);
                    setNextPost(null);

                    if (currentIndex > 0) {
                        setPrevPost(sortedBlogs[currentIndex - 1]);
                    }
                    if (currentIndex !== -1 && currentIndex < sortedBlogs.length - 1) {
                        setNextPost(sortedBlogs[currentIndex + 1]);
                    }

                    setStatusMessage('');
                } else {
                    setBlogPost(null);
                    setAllBlogs([]);
                    setPrevPost(null);
                    setNextPost(null);
                    setImageUrl(null);
                    setSpecificImageUrl(null);
                    setStatusMessage('The blog you are looking for does not exist or may have been removed. Please check the URL or browse our available blogs.');
                }
            } else {
                setBlogPost(null);
                setAllBlogs([]);
                setPrevPost(null);
                setNextPost(null);
                setImageUrl(null);
                setSpecificImageUrl(null);
                setStatusMessage('The blog you are looking for does not exist or may have been removed. Please check the URL or browse our available blogs.');
            }
        } catch (err) {
            setBlogPost(null);
            setAllBlogs([]);
            setPrevPost(null);
            setNextPost(null);
            setImageUrl(null);
            setSpecificImageUrl(null);
            setStatusMessage('The blog you are looking for does not exist or may have been removed. Please check the URL or browse our available blogs.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        findData();
    }, [slug]);

    useEffect(() => {
        const fetchImages = async () => {
            if (!blogPost) {
                setImageUrl(null);
                setSpecificImageUrl(null);
                return;
            }

            if (blogPost.image) {
                const url = await renderImage(blogPost.image);
                setImageUrl(url);
            }

            if (blogPost.specificImage) {
                const url = await renderImage(blogPost.specificImage);
                setSpecificImageUrl(url);
            }
        };

        fetchImages();
    }, [blogPost]);

    const formatDate = (dateValue) => {
        if (!dateValue) return '';
        const dateObj = new Date(dateValue);
        return dateObj.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handlePrevious = () => {
        if (prevPost?.slug) {
            navigate(`/blogs/${prevPost.slug}`);
        }
    };

    const handleNext = () => {
        if (nextPost?.slug) {
            navigate(`/blogs/${nextPost.slug}`);
        }
    };

    const displayImage = specificImageUrl || null;

    if (isLoading) {
        return (
            <section className="bg-[#F0F0E5] min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-4xl">
                    <div className="h-60 sm:h-72 md:h-80 bg-gray-200 rounded-2xl skeleton-load" />
                </div>
            </section>
        );
    }

    if (!blogPost) {
        const message = statusMessage || 'The blog you are looking for does not exist or may have been removed. Please check the URL or browse our available blogs.';
        return (
            <section className="bg-[#F0F0E5] min-h-screen py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-3xl font-bold text-[#F37934] mb-8 font-cinzel">
                        Blog Not Found
                    </h1>
                    <div className="bg-gradient-to-br from-[#F0F0E5] via-[#FFFFFF] to-[#F0F0E5]  mx-auto border border-white rounded-2xl px-6 sm:px-10 py-10 sm:py-12 max-w-3xl mx-auto shadow-sm">
                        <p className="text-[#56453F] w-[80%] text-center mx-auto text-base font-semibold sm:text-lg leading-relaxed mb-6">
                            {message}
                        </p>
                        <button
                            onClick={() => navigate('/blogs')}
                            className="bg-[#F37934] text-white px-5 py-2 rounded-md hover:bg-[#d66a2d] transition-colors text-sm sm:text-base font-medium"
                        >
                            Back to Blogs
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-[#F0F0E5] min-h-screen py-16 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center max-w-sm md:max-w-5xl mx-auto mt-8 md:mt-12 mb-8 px-4">
                    <button
                        onClick={handlePrevious}
                        disabled={!prevPost}
                        className={`flex items-center gap-2 border px-3 py-2 font-bold rounded transition-all duration-300 ${prevPost
                            ? "border-[#F37934] bg-[#F37934] text-white hover:bg-[#d66a2d] hover:scale-105 cursor-pointer"
                            : "border-gray-300 bg-gray-200 text-gray-400 !cursor-not-allowed pointer-events-none"
                            }`}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span className="hidden md:inline">Previous</span>
                    </button>
                    <div className="flex justify-center items-center flex-1 px-4">
                        <h1
                            className="text-lg md:text-3xl font-bold text-center text-[#F37934] uppercase font-cinzel line-clamp-2"
                        >
                            {blogPost?.heading}
                        </h1>
                    </div>
                    <button
                        onClick={handleNext}
                        disabled={!nextPost}
                        className={`flex items-center gap-2 border px-3 py-2 font-bold rounded transition-all duration-300 ${nextPost
                            ? "border-[#F37934] bg-[#F37934] text-white hover:bg-[#d66a2d] hover:scale-105 cursor-pointer"
                            : "border-gray-300 bg-gray-200 text-gray-400 !cursor-not-allowed pointer-events-none"
                            }`}
                    >
                        <span className="hidden md:inline">Next</span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>


                {displayImage && (
                    <div
                        className="mb-8 rounded-2xl overflow-hidden shadow-md"
                        data-aos="zoom-in"
                        data-aos-duration="800"
                    >
                        <img
                            src={displayImage}
                            alt={blogPost?.heading || 'Blog image'}
                            className="w-full h-auto"
                        />
                    </div>
                )}

                <article
                    className=" p-6 md:p-10"
                    data-aos="fade-up"
                    data-aos-duration="800"
                >
                    {blogPost?.date && (
                        <div className="flex items-center text-[#F37934] text-sm mb-6">
                            <img
                                src={dateIcon}
                                alt="Date"
                                className="w-4 h-4 mr-2"
                            />
                            <span className="text-[#56453F]">{formatDate(blogPost.date)}</span>
                        </div>
                    )}

                    <h2 className="text-2xl font-bold text-[#56453F] mb-6 font-cinzel uppercase">
                        {blogPost?.heading}
                    </h2>

                    {blogPost?.content && (
                        <div
                            className="prose prose-lg max-w-none text-[#56453F]"
                            style={{ lineHeight: '1.8' }}
                        >
                            <div
                                className="text-base leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: blogPost.content }}
                            />
                        </div>
                    )}
                </article>
            </div>
        </section>
    );
}
